import { useEffect, useState } from 'react';
import { marked } from 'marked';
import axios from 'axios';
import { PenLine, Trash2, Eye, EyeOff, Plus, ArrowLeft } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tags: string[];
  image_url?: string;
  published: boolean;
  published_at: string;
  created_at: string;
}

const API = import.meta.env.VITE_API_BASE_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const emptyForm = { title: '', excerpt: '', content: '', tags: '', image_url: '', published: false };

export default function BlogEditor() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/blog/admin/all`, { headers: authHeader() });
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const openNew = () => {
    setEditing(null);
    setIsNew(true);
    setForm(emptyForm);
    setPreview(false);
    setError('');
  };

  const openEdit = (post: BlogPost) => {
    setEditing(post);
    setIsNew(false);
    setForm({
      title: post.title,
      excerpt: post.excerpt || '',
      content: post.content,
      tags: post.tags.join(', '),
      image_url: post.image_url || '',
      published: post.published,
    });
    setPreview(false);
    setError('');
  };

  const closeEditor = () => { setEditing(null); setIsNew(false); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      title: form.title,
      excerpt: form.excerpt,
      content: form.content,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      image_url: form.image_url || null,
      published: form.published,
    };
    try {
      if (isNew) {
        await axios.post(`${API}/api/blog`, payload, { headers: authHeader() });
      } else if (editing) {
        await axios.put(`${API}/api/blog/${editing.id}`, payload, { headers: authHeader() });
      }
      await fetchPosts();
      closeEditor();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this post?')) return;
    await axios.delete(`${API}/api/blog/${id}`, { headers: authHeader() });
    fetchPosts();
  };

  const togglePublish = async (post: BlogPost) => {
    await axios.put(`${API}/api/blog/${post.id}`, { published: !post.published }, { headers: authHeader() });
    fetchPosts();
  };

  // Editor view
  if (isNew || editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={closeEditor} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h3 className="font-semibold text-gray-900">{isNew ? 'New Post' : 'Edit Post'}</h3>
          <button
            onClick={() => setPreview(!preview)}
            className="ml-auto flex items-center gap-1.5 text-sm text-gray-500 hover:text-emerald-800"
          >
            {preview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {preview ? 'Edit' : 'Preview'}
          </button>
        </div>

        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

        {preview ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{form.title || 'Untitled'}</h1>
            <div className="flex gap-2 mb-6">
              {form.tags.split(',').filter(Boolean).map(t => (
                <span key={t} className="text-xs font-semibold px-2 py-0.5 bg-[#e8f0ec] text-emerald-800 rounded-full">{t.trim()}</span>
              ))}
            </div>
            <article
              className="prose prose-gray max-w-none"
              dangerouslySetInnerHTML={{ __html: marked(form.content) as string }}
            />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Post title..."
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags <span className="text-gray-400">(comma separated)</span></label>
              <input
                type="text"
                value={form.tags}
                onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                placeholder="AWS, FinOps, Cost Optimization"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image URL <span className="text-gray-400">(optional)</span></label>
              <input
                type="url"
                value={form.image_url}
                onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                placeholder="https://images.unsplash.com/..."
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm"
              />
              {form.image_url && (
                <img src={form.image_url} alt="cover preview" className="mt-2 w-full h-32 object-cover rounded-lg" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt <span className="text-gray-400">(optional)</span></label>
              <textarea
                value={form.excerpt}
                onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                rows={2}
                placeholder="Short description shown on the blog list..."
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content <span className="text-gray-400">(Markdown)</span></label>
              <textarea
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                rows={16}
                placeholder="Write your post in Markdown..."
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm font-mono resize-y"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
                  className="w-4 h-4 rounded text-emerald-800"
                />
                <span className="text-sm font-medium text-gray-700">Publish immediately</span>
              </label>
              <div className="ml-auto flex gap-2">
                <button onClick={closeEditor} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg">
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2 text-sm font-semibold bg-[#0F3D2E] text-white rounded-lg hover:shadow-md disabled:opacity-50 transition-all"
                >
                  {saving ? 'Saving...' : form.published ? 'Publish' : 'Save Draft'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Post list view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Blog Posts <span className="text-gray-400 font-normal">({posts.length})</span></h3>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:shadow-md transition-all"
        >
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3 animate-pulse">
            {[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}
          </div>
        ) : posts.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <p className="mb-3">No posts yet.</p>
            <button onClick={openNew} className="text-emerald-800 font-medium hover:text-blue-700">Write your first post →</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tags</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {posts.map(post => (
                <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900 max-w-xs truncate">{post.title}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-[#e8f0ec] text-emerald-800 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => togglePublish(post)} className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${
                      post.published ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}>
                      {post.published ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-xs">
                    {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => openEdit(post)} className="p-1.5 text-gray-400 hover:text-emerald-800 transition-colors">
                        <PenLine className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(post.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
